// Login-Page. Wired an useAuth.signIn. Pre-Auth-Route (kein AuthGate).
// Design folgt mockups/03-shell — basic Form mit Aurora-Background im Hintergrund.

import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { LogIn, Mail, Lock } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const { signIn, isAuthenticated, loading, error, setError } = useAuth();
  const location = useLocation();
  const fromPath = (location.state as LocationState | null)?.from ?? '/markt';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (isAuthenticated) {
    return <Navigate to={fromPath} replace />;
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Bitte E-Mail und Passwort eingeben.');
      return;
    }
    setSubmitting(true);
    try {
      await signIn(email, password);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="relative grid min-h-dvh place-items-center px-4">
      <div className="aurora">
        <div className="aurora__blob aurora__blob--1"></div>
        <div className="aurora__blob aurora__blob--2"></div>
        <div className="aurora__blob aurora__blob--3"></div>
      </div>
      <div className="aurora-grid"></div>
      <div className="aurora-vignette"></div>

      <form
        onSubmit={handleSubmit}
        className="glass-strong relative z-10 w-full max-w-sm rounded-2xl p-6 text-neutral-100"
      >
        <div className="mb-5 flex flex-col items-center text-center">
          <img
            src="/eve-satoshi.png"
            alt="EVE mit Satoshi"
            className="eve-avatar-pulse mb-3 h-16 w-16 rounded-full object-cover ring-2 ring-amber-500/50"
          />
          <h1 className="text-xl font-semibold">Willkommen bei EVE</h1>
          <p className="mt-1 text-sm text-neutral-400">Melde dich an, um weiterzumachen.</p>
        </div>

        <label className="mb-3 block text-sm">
          <span className="mb-1 block text-xs uppercase tracking-wider text-neutral-500">
            E-Mail
          </span>
          <span className="glass flex items-center gap-2 rounded-lg px-3 py-2">
            <Mail className="size-4 text-neutral-500" />
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-transparent text-sm text-neutral-100 outline-none placeholder:text-neutral-500"
              placeholder="doc@upro.de"
            />
          </span>
        </label>

        <label className="mb-4 block text-sm">
          <span className="mb-1 block text-xs uppercase tracking-wider text-neutral-500">
            Passwort
          </span>
          <span className="glass flex items-center gap-2 rounded-lg px-3 py-2">
            <Lock className="size-4 text-neutral-500" />
            <input
              type="password"
              autoComplete="current-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="flex-1 bg-transparent text-sm text-neutral-100 outline-none placeholder:text-neutral-500"
              placeholder="••••••••"
            />
          </span>
        </label>

        {error ? (
          <p className="mb-3 rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting || loading}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-neutral-950 shadow-[0_0_20px_rgba(251,191,36,0.3)] transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? (
            <span className="tabular">Anmelden…</span>
          ) : (
            <>
              <LogIn className="size-4" />
              <span>Anmelden</span>
            </>
          )}
        </button>

        <div className="mt-4 flex items-center justify-between text-xs text-neutral-400">
          <Link to="/recover" className="hover:text-amber-300 hover:underline">
            Passwort vergessen?
          </Link>
          <Link to="/signup" className="hover:text-amber-300 hover:underline">
            Account erstellen
          </Link>
        </div>
      </form>
    </main>
  );
}
